import { purchaseSeatsTabUIDs as tabUIDs } from "@/global/config/constants";
import { globalConfig } from "@/global/config/global_config";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import { CartManagerState } from "@/global/store/data/purchaseSeats/cartManager";
import {
  EventPackage,
  EventType,
  GroupLevelInventory,
  InventoryType,
} from "@/global/store/data/types";
import { Scheduler } from "@/global/utils/scheduler";

export function calcDefaultPurchaseSeatTab(teamConfig: TeamConfig) {
  const { isPackageEnabled } = teamConfig?.packageConfigs;
  return isPackageEnabled ? tabUIDs.packages : tabUIDs.findGames;
}

export function calcIsEventCardDisabled(
  event: EventType,
  selectedPkg: EventPackage | null,
  teamConfig: TeamConfig,
  cart?: CartManagerState["cart"],
  inventories?: InventoryType[]
) {
  const { isPackageEnabled, canRemovePackageGames } = teamConfig.packageConfigs;
  // allow remove if not package enabled
  if (!isPackageEnabled) return false;

  // allow remove if inventories length is 0
  if (inventories?.length == 0) return false;

  // optional cart error message checking, allow remove event if error message is present
  let cartEvent = cart?.find((evt) => evt?.eventCode === event.eventCode);
  if (cartEvent?.errorMessage) return false;

  // allow remove if event is not in package or package is build your own
  let isEventInPkg = false;
  if (!selectedPkg?.isBuildYourOwn && selectedPkg?.events) {
    isEventInPkg = selectedPkg.events?.some((evt) => {
      return evt.eventCode === event.eventCode;
    });
  }

  const isDisabled = isPackageEnabled && !canRemovePackageGames && isEventInPkg;
  return isDisabled;
}

export function createCombinedSeats(seats: GroupLevelInventory["seats"]) {
  // First, group seats by row
  const rows = {} as Record<string, number[]>;

  for (const seat of seats) {
    if (!rows[seat.rowName]) {
      rows[seat.rowName] = [];
    }
    rows[seat.rowName].push(seat.seatNumber);
  }

  const result = [];

  // Process each row
  for (const rowName in rows) {
    const seatNumbers = rows[rowName];

    // Sort numbers numerically
    seatNumbers.sort((a, b) => a - b);

    let start = seatNumbers[0];
    let prev = seatNumbers[0];

    // If there's only one seat in the row
    if (seatNumbers.length === 1) {
      result.push({
        rowName,
        firstSeat: start,
        lastSeat: null,
      });
      continue;
    }

    // Process multiple seats
    for (let i = 1; i < seatNumbers.length; i++) {
      const current = seatNumbers[i];

      // If current is consecutive with previous
      if (current === prev + 1) {
        prev = current;

        // If this is the last seat, push the range
        if (i === seatNumbers.length - 1) {
          result.push({
            rowName,
            firstSeat: start,
            lastSeat: current,
          });
        }
      } else {
        // Push the current range (or single seat)
        result.push({
          rowName,
          firstSeat: start,
          lastSeat: prev === start ? null : prev,
        });

        // Start a new range
        start = current;
        prev = current;

        // If this is the last seat, push it as single
        if (i === seatNumbers.length - 1) {
          result.push({
            rowName,
            firstSeat: current,
            lastSeat: null,
          });
        }
      }
    }
  }

  return result;
}
